import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { 
  Award, 
  DollarSign, 
  Settings, 
  ArrowRight, 
  CheckCircle, 
  Star,
  Users,
  Clock,
  Shield
} from 'lucide-react';

const Home = () => {
  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/team_working_0.jpg"
            alt="Team working"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 video-overlay"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <div className="max-w-4xl mx-auto space-y-8">
            <Badge className="bg-accent/20 text-accent border-accent/30 font-body">
              Celebrating 20 Years of Excellence
            </Badge>
            
            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold leading-tight">
              20 Years of Building the Web.
              <span className="block text-accent">
                Now, We're Building Your Business.
              </span>
            </h1>
            
            <p className="font-body text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
              From humble beginnings in 2005 to today's cutting-edge solutions. 
              We create modern, affordable websites that put you in complete control.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                asChild 
                size="lg" 
                className="bg-accent hover:bg-accent/90 text-accent-foreground font-heading font-semibold text-lg px-8 py-6"
              >
                <Link to="/booking">
                  Start Your Project
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              
              <Button 
                asChild 
                variant="outline" 
                size="lg" 
                className="border-white/30 text-white hover:bg-white/10 font-heading font-semibold text-lg px-8 py-6"
              >
                <Link to="/case-studies">View Our Work</Link>
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center items-center gap-8 pt-8">
              <div className="flex items-center space-x-2">
                <Star className="h-5 w-5 text-accent fill-current" />
                <span className="font-body text-white/80">500+ Happy Clients</span>
              </div>
              <div className="flex items-center space-x-2">
                <Award className="h-5 w-5 text-accent" />
                <span className="font-body text-white/80">20 Years Experience</span>
              </div>
              <div className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-accent" />
                <span className="font-body text-white/80">100% Satisfaction</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/60 rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* Why Section */}
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="bg-primary/10 text-primary border-primary/20 font-body mb-4">
              Why Choose SuccessPro Digital
            </Badge>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground mb-6">
              The Perfect Balance of
              <span className="text-gradient"> Experience, Value & Control</span>
            </h2>
            <p className="font-body text-lg text-muted-foreground max-w-3xl mx-auto">
              After two decades in web development, we've learned what truly matters: 
              delivering exceptional websites that don't break the bank and empowering you to manage them yourself.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Experience */}
            <Card className="hover-lift shadow-card border-0 bg-white">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Award className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-foreground mb-4">Experience</h3>
                <p className="font-body text-muted-foreground mb-6">
                  Two decades of web development expertise. We've seen every trend, 
                  survived every algorithm change, and mastered every platform evolution.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-accent" />
                    <span className="font-body text-sm">500+ Websites Built</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-accent" />
                    <span className="font-body text-sm">20 Years in Business</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-accent" />
                    <span className="font-body text-sm">Industry Certified</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Affordability */}
            <Card className="hover-lift shadow-card border-0 bg-white">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <DollarSign className="h-8 w-8 text-accent" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-foreground mb-4">Affordability</h3>
                <p className="font-body text-muted-foreground mb-6">
                  Premium quality doesn't require premium prices. Our streamlined process 
                  and 20 years of efficiency mean better websites for less money.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-accent" />
                    <span className="font-body text-sm">No Hidden Fees</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-accent" />
                    <span className="font-body text-sm">Transparent Pricing</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-accent" />
                    <span className="font-body text-sm">Payment Plans Available</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Control */}
            <Card className="hover-lift shadow-card border-0 bg-white">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Settings className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-foreground mb-4">Control</h3>
                <p className="font-body text-muted-foreground mb-6">
                  Your website, your rules. We build it, then we teach you how to manage it. 
                  No more being held hostage by your web developer.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-accent" />
                    <span className="font-body text-sm">Full Admin Access</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-accent" />
                    <span className="font-body text-sm">Training Included</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-accent" />
                    <span className="font-body text-sm">No Lock-in Contracts</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-padding bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="space-y-2">
              <div className="font-heading text-4xl md:text-5xl font-bold text-accent">500+</div>
              <div className="font-body text-primary-foreground/80">Websites Built</div>
            </div>
            <div className="space-y-2">
              <div className="font-heading text-4xl md:text-5xl font-bold text-accent">20</div>
              <div className="font-body text-primary-foreground/80">Years Experience</div>
            </div>
            <div className="space-y-2">
              <div className="font-heading text-4xl md:text-5xl font-bold text-accent">98%</div>
              <div className="font-body text-primary-foreground/80">Client Satisfaction</div>
            </div>
            <div className="space-y-2">
              <div className="font-heading text-4xl md:text-5xl font-bold text-accent">24/7</div>
              <div className="font-body text-primary-foreground/80">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-accent/10 to-primary/10">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground">
              Ready to Take Control of Your
              <span className="text-gradient"> Digital Presence?</span>
            </h2>
            <p className="font-body text-lg text-muted-foreground">
              Join hundreds of businesses who've discovered the perfect balance of 
              professional web development and complete control over their online presence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild 
                size="lg" 
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-semibold text-lg px-8 py-6"
              >
                <Link to="/booking">
                  Schedule Free Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button 
                asChild 
                variant="outline" 
                size="lg" 
                className="font-heading font-semibold text-lg px-8 py-6"
              >
                <Link to="/services">View Our Services</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;